import { EditorView } from '@codemirror/view';
import { Transaction } from '@codemirror/state';

/**
 * Format dependencies as text for claim references
 * @param {number[]} deps - Array of claim numbers
 * @returns {string} - Formatted dependency text (e.g., "1-3" or "1, 3, and 5")
 */
export function formatDependencies(deps) {
    if (!deps || deps.length === 0) return '';
    if (deps.length === 1) return deps[0].toString();
    
    // Sort dependencies
    const sorted = [...deps].sort((a, b) => a - b);
    
    // Check if consecutive for range format
    const isConsecutive = sorted.every((num, i) => 
        i === 0 || num === sorted[i - 1] + 1
    );
    
    if (isConsecutive && sorted.length > 2) {
        // Format as range
        return `${sorted[0]}-${sorted[sorted.length - 1]}`;
    }
    
    // Format as list
    if (sorted.length === 2) {
        return `${sorted[0]} or ${sorted[1]}`;
    }
    
    return sorted.slice(0, -1).join(', ') + ', and ' + sorted[sorted.length - 1];
}

/**
 * Find the dependency text in a claim and get its position
 * @param {string} text - Full document text
 * @param {number} claimNum - Claim number
 * @returns {Object|null} - Object with start, end, and current text, or null if not found
 */
function findDependencyPosition(text, claimNum) {
    const patterns = [
        // "The method of claim X or Y" - CHECK THIS FIRST before single claim
        new RegExp(`(${claimNum}\\.)\\s+(The\\s+(?:method|system|device|apparatus|process|composition|article)\\s+of\\s+)(claim\\s+\\d+\\s+or\\s+\\d+)`, 'i'),
        // "The method of claim X"
        new RegExp(`(${claimNum}\\.)\\s+(The\\s+(?:method|system|device|apparatus|process|composition|article)\\s+of\\s+)(claim\\s+\\d+)`, 'i'),
        // "The method of any of claims X-Y"
        new RegExp(`(${claimNum}\\.)\\s+(The\\s+(?:method|system|device|apparatus|process|composition|article)\\s+of\\s+any\\s+of\\s+)(claims\\s+\\d+-\\d+)`, 'i'),
        // "The method of claims X and Y"
        new RegExp(`(${claimNum}\\.)\\s+(The\\s+(?:method|system|device|apparatus|process|composition|article)\\s+of\\s+)(claims\\s+\\d+\\s+and\\s+\\d+)`, 'i'),
        // "The method according to claim X"
        new RegExp(`(${claimNum}\\.)\\s+(The\\s+(?:method|system|device|apparatus|process|composition|article)\\s+according\\s+to\\s+)(claim\\s+\\d+)`, 'i'),
        // "The method of any preceding claim"
        new RegExp(`(${claimNum}\\.)\\s+(The\\s+(?:method|system|device|apparatus|process|composition|article)\\s+of\\s+)(any\\s+preceding\\s+claim)`, 'i'),
        // "A method according to any one of claims X to Y"
        new RegExp(`(${claimNum}\\.)\\s+((?:The|A)\\s+(?:method|system|device|apparatus|process|composition|article)\\s+according\\s+to\\s+any\\s+one\\s+of\\s+)(claims\\s+\\d+\\s+to\\s+\\d+)`, 'i')
    ];
    
    for (const pattern of patterns) {
        const match = pattern.exec(text);
        if (match) {
            const claimNumAndDot = match[1];
            const prefix = match[2];
            const claimRef = match[3];
            
            const spaceAfterDot = text[match.index + claimNumAndDot.length] === ' ' ? 1 : 0;
            const start = match.index + claimNumAndDot.length + spaceAfterDot + prefix.length;
            const end = start + claimRef.length;
            
            return {
                start,
                end,
                currentText: claimRef,
                prefix: prefix,
                fullMatch: match[0]
            };
        }
    }
    
    const independentPattern = new RegExp(`(${claimNum}\\.)\\s+(A|An|The)\\s+`, 'i');
    const indMatch = independentPattern.exec(text);
    if (indMatch) {
        return {
            start: indMatch.index + indMatch[1].length + 1,
            end: indMatch.index + indMatch[1].length + 1 + indMatch[2].length,
            currentText: indMatch[2],
            prefix: '',
            needsInsertion: true,
            fullMatch: indMatch[0]
        };
    }
    
    return null;
}

/**
 * Apply suggested dependencies to a claim
 * @param {EditorView} view - CodeMirror editor view
 * @param {number} claimNum - Claim number to update
 * @param {number[]} suggestedDeps - Suggested dependency claim numbers
 * @returns {boolean} - True if replacement was successful
 */
export function applySuggestedDependencies(view, claimNum, suggestedDeps) {
    const doc = view.state.doc;
    const text = doc.toString();
    
    const depPosition = findDependencyPosition(text, claimNum);
    if (!depPosition) {
        return false;
    }
    
    // Format the new dependencies
    const formattedDeps = formatDependencies(suggestedDeps);
    
    let changes;
    if (depPosition.needsInsertion) {
        const typeMatch = /The\s+(method|system|device|apparatus|process|composition|article)\s+of/i.exec(text);
        const type = typeMatch ? typeMatch[1] : 'method';
        
        const newText = suggestedDeps.length > 1 
            ? `The ${type} of any of claims ${formattedDeps}`
            : `The ${type} of claim ${formattedDeps}`;
        
        changes = [{
            from: depPosition.start,
            to: depPosition.end,
            insert: newText
        }];
    } else {
        const claimWordMatch = /^(claims?\s+)/i.exec(depPosition.currentText);
        const _claimWord = claimWordMatch ? claimWordMatch[1] : 'claim ';

        let newDepText;
        if (suggestedDeps.length === 1) {
            newDepText = `claim ${suggestedDeps[0]}`;
        } else {
            newDepText = `claims ${formattedDeps}`;
        }
        
        changes = [{
            from: depPosition.start,
            to: depPosition.end,
            insert: newDepText
        }];
    }
    
    if (window.setForceHeavyRefreshNext) {
        window.setForceHeavyRefreshNext();
    }
    
    view.dispatch({
        changes: changes,
        annotations: Transaction.userEvent.of('claim-dependency.accept')
    });
    
    return true;
}

/**
 * Handle click on dependency suggestion
 * @param {EditorView} view - CodeMirror editor view
 * @param {Event} event - Click event
 * @returns {boolean} - True if click was handled
 */
export function handleDependencySuggestionClick(view, event) {
    const target = event.target;
    
    const suggestionElement = target.closest('.claim-dependency-suggestion');
    const outdatedElement = target.closest('.claim-dependency-outdated');
    
    if (!suggestionElement && !outdatedElement) {
        return false;
    }
    
    let claimNum, suggestedDeps;
    
    if (suggestionElement) {
        const suggested = suggestionElement.dataset.suggested;
        if (!suggested) return false;
        
        suggestedDeps = JSON.parse(suggested);
        const parent = suggestionElement.parentElement?.closest('.claim-dependency-outdated');
        claimNum = parent ? parseInt(parent.dataset.claim) : null;
    } else {
        claimNum = parseInt(outdatedElement.dataset.claim);
        const suggested = outdatedElement.dataset.suggestedDeps;
        if (!suggested) return false;
        
        suggestedDeps = JSON.parse(suggested);
    }
    
    if (!claimNum || !suggestedDeps || suggestedDeps.length === 0) {
        return false;
    }
    
    applySuggestedDependencies(view, claimNum, suggestedDeps);
    
    event.preventDefault();
    event.stopPropagation();
    return true;
}

/**
 * Handle keyboard shortcut for accepting suggestions
 * @param {EditorView} view - CodeMirror editor view
 * @returns {boolean} - True if suggestion was accepted
 */
export function acceptSuggestionAtCursor(view) {
    const selection = view.state.selection.main;
    const pos = selection.head;
    
    // Find if cursor is within an outdated dependency
    const decorations = view.state.field(view.state.facet(EditorView.decorations));
    let foundSuggestion = null;
    
    decorations.forEach(deco => {
        if (deco.from <= pos && deco.to >= pos) {
            const spec = deco.spec;
            if (spec && spec.class === 'claim-dependency-outdated') {
                const claimNum = parseInt(spec.attributes['data-claim']);
                const suggestedDeps = JSON.parse(spec.attributes['data-suggested-deps']);
                foundSuggestion = { claimNum, suggestedDeps };
            }
        }
    });
    
    if (foundSuggestion) {
        return applySuggestedDependencies(view, foundSuggestion.claimNum, foundSuggestion.suggestedDeps);
    }
    
    return false;
}